import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import styled from 'styled-components';

import { useStudentAcademic } from 'core/hooks';

import { TabStateView } from './TabStateView';
import { StudentColors } from './studentColors';

interface IAcademicPanelProps {
  studentId: string;
}

/**
 * Academic «how the student studies» panel for the Student card (Area 3,
 * Req 7–8). A DEDICATED surface, always separate from the financial blocks
 * (Req 8.1), so enabling it never reshuffles the money content.
 *
 * Gated behind Backend_Academic_Dependency + the `STUDENT_ACADEMIC` flag: while
 * unavailable it renders a localized gated-off note (Req 7.5). When available it
 * shows attendance % + average mark and the chronological lesson history with
 * loading/empty/error states that fail independently of the rest of the card
 * (Req 7.1–7.4, 8.2). Threshold colors come from `StudentColors` (Req 8.3).
 */
export const AcademicPanel = memo(({ studentId }: IAcademicPanelProps) => {
  const { t } = useTranslation();
  const academic = useStudentAcademic(studentId);

  if (!academic.available) {
    return (
      <Card>
        <Title>{t('pages.studentDetail.academic.title')}</Title>
        <Muted>{t('pages.studentDetail.academic.gatedOff')}</Muted>
      </Card>
    );
  }

  const { loading, error, data, refetch } = academic;
  const lessons = data?.lessons ?? [];

  return (
    <Card>
      <Title>{t('pages.studentDetail.academic.title')}</Title>
      <TabStateView
        loading={loading && !data}
        error={error}
        isEmpty={!loading && lessons.length === 0}
        onRetry={refetch}
      >
        <Metrics>
          <Metric>
            <MetricLabel>
              {t('pages.studentDetail.academic.attendance')}
            </MetricLabel>
            <MetricValue>
              {data?.attendancePercent != null
                ? `${data.attendancePercent}%`
                : '—'}
            </MetricValue>
          </Metric>
          <Metric>
            <MetricLabel>
              {t('pages.studentDetail.academic.averageMark')}
            </MetricLabel>
            <MetricValue>
              {data?.averageMark != null ? data.averageMark : '—'}
            </MetricValue>
          </Metric>
        </Metrics>

        <LessonList>
          {lessons.map((lesson) => (
            <LessonRow key={lesson.id}>
              <span>
                {lesson.lessonNumber != null ? `#${lesson.lessonNumber} ` : ''}
                {lesson.name || ''}
              </span>
              <LessonMeta>
                {moment(lesson.date).format('DD.MM.YYYY')}
                {lesson.attended != null
                  ? ` · ${
                      lesson.attended
                        ? t('pages.studentDetail.academic.attended')
                        : t('pages.studentDetail.academic.absent')
                    }`
                  : ''}
                {lesson.mark != null ? ` · ${lesson.mark}` : ''}
              </LessonMeta>
            </LessonRow>
          ))}
        </LessonList>
      </TabStateView>
    </Card>
  );
});

const Card = styled.div`
  margin-top: 12px;
  padding: 14px 16px;
  border: 1px solid ${StudentColors.border};
  border-radius: 10px;
  background: ${StudentColors.surface};
`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${StudentColors.textPrimary};
  margin-bottom: 8px;
`;

const Muted = styled.div`
  font-size: 13px;
  color: ${StudentColors.textMuted};
`;

const Metrics = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 12px;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MetricLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${StudentColors.textMuted};
`;

const MetricValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: ${StudentColors.textPrimary};
`;

const LessonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const LessonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: ${StudentColors.textSecondary};
`;

const LessonMeta = styled.span`
  color: ${StudentColors.textMuted};
  white-space: nowrap;
`;
